import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils';

export const AuthContext = createContext(null);

// Set up axios to always send the Authorization header if token exists
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Axios response interceptor to handle token expiry (401 Unauthorized)
let logoutCallback = null;
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      if (logoutCallback) logoutCallback();
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // RBAC: the authenticated user's profile (role) and effective permissions.
  const [profile, setProfile] = useState(null);
  const [permissions, setPermissions] = useState([]);
  // Platform (super-admin tier) permission codes — drives delegated-admin gating.
  const [platformPermissions, setPlatformPermissions] = useState([]);
  // External tools the super admin has enabled for the active org (om/ciso).
  const [integrations, setIntegrations] = useState({});

  // Pull the canonical profile + permission set from the backend.
  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/auth/me`);
      setProfile(res.data);
      setPermissions(res.data.permissions || []);
      setPlatformPermissions(res.data.platform_permissions || []);
      setIntegrations(res.data.integrations || {});
      return res.data;
    } catch (err) {
      setProfile(null);
      setPermissions([]);
      setPlatformPermissions([]);
      setIntegrations({});
      return null;
    }
  }, []);

  // Apply the auth payload returned by login/register/google-login.
  const applyAuthPayload = useCallback((data) => {
    setUser({ token: data.access_token });
    localStorage.setItem('token', data.access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
    if (Array.isArray(data.permissions)) setPermissions(data.permissions);
    if (Array.isArray(data.platform_permissions)) setPlatformPermissions(data.platform_permissions);
    if (data.integrations) setIntegrations(data.integrations);
    if (data.user || data.role) {
      setProfile({ ...(data.user || {}), role: data.role, permissions: data.permissions || [] });
    }
  }, []);

  // Guards the one-time session bootstrap. Without it React 18 StrictMode
  // double-invokes this effect in dev, firing /api/auth/me twice on every load.
  const didBootstrap = useRef(false);

  // On mount, restore session from localStorage and refresh the profile.
  useEffect(() => {
    if (didBootstrap.current) return;
    didBootstrap.current = true;
    const existing = localStorage.getItem('token');
    if (existing) {
      setUser({ token: existing });
      axios.defaults.headers.common['Authorization'] = `Bearer ${existing}`;
      fetchProfile().finally(() => setLoading(false));
    } else {
      setUser(null);
      setLoading(false);
    }
  }, [fetchProfile]);

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, { email, password });
      if (response.data && response.data.access_token) {
        applyAuthPayload(response.data);
        // Load the full profile (roles, permissions) so the post-login role
        // chooser has what it needs.
        await fetchProfile();
        setLoading(false);
        return { token: response.data.access_token };
      }
      throw new Error('Invalid email or password');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
      setLoading(false);
      throw err;
    }
  };

  const register = async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/register`, data);
      if (response.data && response.data.access_token) {
        applyAuthPayload(response.data);
      }
      setLoading(false);
      return response.data;
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
      throw err;
    }
  };

  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      setError(null);
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Best-effort: revoke any comm-gateway SSO tokens for this user so logging
    // out of the CMS also cuts gateway access (fire with the token before it's
    // cleared; don't block logout on it).
    const token = localStorage.getItem('token');
    if (token) {
      axios.post(`${API_BASE_URL}/api/comm-gateway/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => { /* ignore */ });
    }
    setUser(null);
    setProfile(null);
    setPermissions([]);
    localStorage.removeItem('token');
    try { sessionStorage.removeItem('panelMode'); } catch (e) { /* ignore */ }
    delete axios.defaults.headers.common['Authorization'];
  };

  // Set (or clear, with roleId=null) the active role: re-issues the token and
  // refreshes the profile so permissions reflect the chosen role.
  const selectRole = useCallback(async (roleId) => {
    const res = await axios.post(`${API_BASE_URL}/api/roles/select`, { role_id: roleId });
    if (res.data?.access_token) {
      localStorage.setItem('token', res.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      setUser({ token: res.data.access_token });
      await fetchProfile();
      return res.data;
    }
    throw new Error('Select-role response missing access_token');
  }, [fetchProfile]);

  // Register logout callback for interceptor
  useEffect(() => {
    logoutCallback = logout;
    return () => {
      logoutCallback = null;
    };
  }, []);

  const googleLogin = async (googleCredential) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.post(`${API_BASE_URL}/api/auth/google-login`, {
        credential: googleCredential,
      });
      if (response.data && response.data.access_token) {
        applyAuthPayload(response.data);
        // Load the full profile (dual-access, roles, permissions) so the
        // post-login panel/role selector has what it needs — same as /login.
        await fetchProfile();
        setLoading(false);
        return { token: response.data.access_token };
      }
      throw new Error('Google login failed');
    } catch (err) {
      setError(err.response?.data?.error || 'Google login failed. Please try again.');
      setLoading(false);
      throw err;
    }
  };

  // RBAC helper. Backend expands wildcards into concrete codes, so a simple
  // membership test is sufficient. No role bypass — super_admin's menu reflects
  // its real (platform-only) permissions; platform-only nav uses isSuperAdmin.
  const hasPermission = useCallback(
    (code) => {
      if (!code) return true;
      return permissions.includes(code);
    },
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (codes = []) => codes.length === 0 || codes.some((c) => hasPermission(c)),
    [hasPermission]
  );

  // Platform (super-admin tier) permission check. super_admin holds every
  // platform code, so it passes any check.
  const hasPlatformPermission = useCallback(
    (...codes) => codes.length === 0 || codes.some((c) => platformPermissions.includes(c)),
    [platformPermissions]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        profile,
        role: profile?.role || null,
        permissions,
        hasPermission,
        hasAnyPermission,
        platformPermissions,
        hasPlatformPermission,
        isPlatformAdmin: !!profile?.is_platform_admin || (platformPermissions?.length || 0) > 0,
        roles: profile?.roles || [],
        activeRoleId: profile?.active_role_id || null,
        selectRole,
        integrations,
        hasIntegration: (provider) => !!integrations?.[provider],
        isSuperAdmin: !!profile?.is_super_admin || profile?.role === 'super_admin',
        isDualAccess: !!profile?.is_dual_access,
        refreshProfile: fetchProfile,
        login,
        register,
        forgotPassword,
        logout,
        googleLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Hook: returns whether the current user holds a permission code.
export const usePermission = (code) => {
  const { hasPermission } = useAuth();
  return hasPermission(code);
};

// Component gate: render children only if the user holds the permission(s).
// <Can perm="roles:create">…</Can>  or  <Can anyOf={['roles:edit','roles:create']}>…</Can>
export const Can = ({ perm, anyOf, fallback = null, children }) => {
  const { hasPermission, hasAnyPermission } = useAuth();
  const ok = anyOf ? hasAnyPermission(anyOf) : hasPermission(perm);
  return ok ? <>{children}</> : fallback;
};
